import { useState, useEffect, FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { validateContactMessage } from "../utils/validation";
import { usePageTitle } from "../hooks/usePageTitle";

interface Settings {
  contact_email?: string;
  contact_phone?: string;
  contact_hours?: string;
}

export default function ContactUs() {
  usePageTitle("Contact Us");
  const { customer, isAuthenticated } = useCustomerAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [settings, setSettings] = useState<Settings>({});
  const [formData, setFormData] = useState({
    subject: "General Inquiry",
    orderNumber: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchSettings();

    // Pre-fill order number if coming from order detail page
    const state = location.state as any;
    if (state?.orderNumber) {
      setFormData((prev) => ({
        ...prev,
        orderNumber: state.orderNumber,
        subject: "Order Inquiry",
      }));
    }
  }, [location]);

  const fetchSettings = async () => {
    try {
      const response = await fetch("/api/settings");
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!isAuthenticated) {
      navigate("/login", { state: { from: "/contact" } });
      return;
    }

    // Validate message with comprehensive validation
    const validationError = validateContactMessage(formData.message);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          subject: formData.subject,
          orderNumber: formData.orderNumber || null,
          message: formData.message,
        }),
      });

      if (response.ok) {
        setSuccess(true);
        setFormData({
          subject: "General Inquiry",
          orderNumber: "",
          message: "",
        });

        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to send message");
      }
    } catch (error) {
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold mb-8">Contact Us</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        {/* Contact Information */}
        <div className="card">
          <h2 className="text-xl font-bold mb-6">Get In Touch</h2>
          <div className="space-y-4">
            {settings.contact_email && (
              <div>
                <h3 className="font-medium mb-1">Email</h3>
                <a
                  href={`mailto:${settings.contact_email}`}
                  className="text-black underline hover:no-underline"
                >
                  {settings.contact_email}
                </a>
              </div>
            )}

            {settings.contact_phone && (
              <div>
                <h3 className="font-medium mb-1">Phone</h3>
                <a
                  href={`tel:${settings.contact_phone}`}
                  className="text-black underline hover:no-underline"
                >
                  {settings.contact_phone}
                </a>
              </div>
            )}

            {settings.contact_hours && (
              <div>
                <h3 className="font-medium mb-1">Business Hours</h3>
                <p className="text-gray-600 whitespace-pre-line">
                  {settings.contact_hours}
                </p>
              </div>
            )}

            {!settings.contact_email && !settings.contact_phone && !settings.contact_hours && (
              <p className="text-gray-600">
                Please use the contact form to reach us.
              </p>
            )}
          </div>
        </div>

        {/* Why Contact Us */}
        <div className="card bg-gray-50">
          <h2 className="text-xl font-bold mb-4">How Can We Help?</h2>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2">
              <span className="text-xl">•</span>
              <span>Questions about your order or shipping</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-xl">•</span>
              <span>Product inquiries and recommendations</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-xl">•</span>
              <span>Return or exchange requests</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-xl">•</span>
              <span>General questions and feedback</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Contact Form */}
      <div className="card">
        <h2 className="text-2xl font-bold mb-6">Send Us a Message</h2>

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded mb-6">
            <p className="font-medium mb-1">Message sent successfully!</p>
            <p className="text-sm">
              We'll respond to your inquiry as soon as possible. You can track your message in your{" "}
              <Link to="/account" className="underline hover:no-underline">
                account dashboard
              </Link>
              .
            </p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {!isAuthenticated ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
            <p className="text-lg font-medium mb-4">Please log in to contact us</p>
            <p className="text-gray-600 mb-6">
              We require customers to be logged in to prevent spam and to better track your inquiries.
            </p>
            <div className="flex gap-4 justify-center">
              <Link
                to="/login"
                state={{ from: "/contact" }}
                className="btn-primary"
              >
                Login
              </Link>
              <Link
                to="/register"
                state={{ from: "/contact" }}
                className="btn-secondary"
              >
                Create Account
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Customer Info (Read-only) */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-2">Sending as:</p>
              <p className="font-medium">
                {customer?.firstName} {customer?.lastName}
              </p>
              <p className="text-sm text-gray-600">{customer?.email}</p>
            </div>

            {/* Subject */}
            <div>
              <label htmlFor="subject" className="block text-sm font-medium mb-1">
                Subject <span className="text-red-500">*</span>
              </label>
              <select
                id="subject"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="input w-full"
                disabled={loading}
                required
              >
                <option value="General Inquiry">General Inquiry</option>
                <option value="Order Inquiry">Order Inquiry</option>
                <option value="Product Question">Product Question</option>
                <option value="Return Request">Return Request</option>
                <option value="Shipping Question">Shipping Question</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Order Number (Optional) */}
            <div>
              <label htmlFor="orderNumber" className="block text-sm font-medium mb-1">
                Order Number <span className="text-gray-500">(optional)</span>
              </label>
              <input
                type="text"
                id="orderNumber"
                value={formData.orderNumber}
                onChange={(e) => setFormData({ ...formData, orderNumber: e.target.value })}
                className="input w-full"
                placeholder="e.g., ORD-1234567890"
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                If your inquiry is about a specific order, please include the order number
              </p>
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium mb-1">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="input w-full"
                rows={8}
                placeholder="Please describe your inquiry in detail..."
                disabled={loading}
                required
              />
            </div>

            {/* Submit Button */}
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex-1"
              >
                {loading ? "Sending..." : "Send Message"}
              </button>
              <Link to="/account" className="btn-secondary">
                Cancel
              </Link>
            </div>

            <p className="text-xs text-gray-500 text-center">
              We typically respond within 24-48 hours during business days.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
